import { useCallback, useEffect, useRef, useState } from 'react';
import { usePortalQuery } from '../usePortalQuery';
import {
  getActiveRestaurants,
  getActiveOrders,
} from '../../services/DeliveryOperationsPortalService';
import { useMapContext } from '../../context/MapContext';
import { usePortalAccess } from '../usePortalAccess';

const DEFAULT_TOGGLES = {
  drivers: true,
  restaurants: false,
  orders: true,
  unassignedOnly: false,
  pickupSoon: false,
};

const buildRestaurantMarkers = (restaurants) =>
  (restaurants || [])
    .filter((r) => r.lat != null && r.lon != null)
    .map((r) => ({
      id: `restaurant-${r.id}`,
      markerType: 'restaurant',
      latitude: r.lat,
      longitude: r.lon,
      text: r.name,
      tempClosed: r.tempClosed,
    }));

const isToday = (utcDateStr) => {
  if (!utcDateStr) return false;
  const orderDate = new Date(utcDateStr.replace(' ', 'T') + 'Z');
  const now = new Date();
  return (
    orderDate.getFullYear() === now.getFullYear() &&
    orderDate.getMonth() === now.getMonth() &&
    orderDate.getDate() === now.getDate()
  );
};

const filterOrders = (orders, toggles) => {
  if (!orders) return [];

  const now = new Date();
  const thirtyMinFromNow = new Date(now.getTime() + 30 * 60 * 1000);

  return orders.filter((o) => {
    if (!isToday(o.pickupAt)) return false;
    if (o.orderType !== 'delivery') return false;
    if (o.orderStatusId >= 10) return false;
    if (toggles.unassignedOnly && o.driverUserId != null) return false;
    if (toggles.pickupSoon) {
      if (!o.pickupAt) return false;
      const pickup = new Date(o.pickupAt.replace(' ', 'T') + 'Z');
      if (pickup < now || pickup > thirtyMinFromNow) return false;
    }
    return true;
  });
};

const buildOrderMarkers = (orders) =>
  (orders || [])
    .filter((o) => o.restaurantLat != null && o.restaurantLon != null)
    .map((o) => ({
      id: `order-${o.id}`,
      markerType: 'order',
      latitude: o.restaurantLat,
      longitude: o.restaurantLon,
      text: o.displayId || String(o.id),
      status: o.orderStatusName,
      order: o,
    }));

export function useMapLayers({ driverMarkers }) {
  const { dispatcher, sessionVersion } = usePortalAccess();
  const [layerToggles, setLayerToggles] = useState(DEFAULT_TOGGLES);
  const { updateMarkers } = useMapContext();
  const hasInitialCentered = useRef(false);
  const pendingCenter = useRef(false);

  const { data: restaurantData, error: restaurantError } = usePortalQuery(
    ['active-restaurants', sessionVersion],
    getActiveRestaurants,
    {
      enabled: dispatcher,
      interval: 60000,
    }
  );

  const { data: orderData, error: orderError } = usePortalQuery(['active-orders', sessionVersion], getActiveOrders, {
    enabled: dispatcher,
    interval: 30000,
  });

  const requestCenter = useCallback(() => {
    pendingCenter.current = true;
  }, []);

  const onLayerToggle = useCallback((key) => {
    setLayerToggles((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  useEffect(() => {
    const filteredOrders = layerToggles.orders && !orderError
      ? buildOrderMarkers(filterOrders(orderData, layerToggles))
      : [];

    const merged = !dispatcher ? [] : [
      ...(layerToggles.drivers ? driverMarkers || [] : []),
      ...(layerToggles.restaurants && !restaurantError
        ? buildRestaurantMarkers(restaurantData)
        : []),
      ...filteredOrders,
    ];

    const isFirstLoad =
      !hasInitialCentered.current && (driverMarkers || []).length > 0;

    const shouldCenter = isFirstLoad || pendingCenter.current;

    if (isFirstLoad) {
      hasInitialCentered.current = true;
    }
    pendingCenter.current = false;

    updateMarkers(merged, {
      centerAfterUpdate: shouldCenter,
      shouldZoom: shouldCenter,
    });
  }, [dispatcher, restaurantError, orderError, layerToggles, driverMarkers, restaurantData, orderData, updateMarkers]);

  return { layerToggles, onLayerToggle, requestCenter };
}
